import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { FlagsmithProvider, useFlagsmith } from '@flagsmith/flagsmith/react'
import flagsmith from '@flagsmith/flagsmith/isomorphic'
import { Project } from 'common/project'
import { useUser } from 'common/services/useUser'
import Loader from './base/Loader'
import { useDevSettings } from 'common/services/useDevSettings'
import { useHydrated } from 'common/services/useHydrated'
import { useDefaultSubscription } from 'common/services/useDefaultSubscription'

type FeatureFlagProviderType = {
  children: React.ReactNode
}

const FeatureFlagProvider: FC<FeatureFlagProviderType> = ({ children }) => {
  const [_, setReady] = useState(Date.now())

  const { data } = useUser({})
  const { subscriptionDetail } = useDefaultSubscription()
  const hydrated = useHydrated()
  const { devSettings } = useDevSettings()
  const whitelabel = devSettings.whitelabel || undefined
  const flagsmithWhitelabel = flagsmith.getTrait('whitelabel') || undefined
  const username = data?.username
  const loadingState = flagsmith.loadingState
  const ready = useMemo(() => loadingState?.source !== 'NONE', [loadingState])

  useEffect(() => {
    if (!ready) return
    if (flagsmith.identity !== username || flagsmithWhitelabel !== whitelabel) {
      if (username) {
        flagsmith.identify(username, {
          email: username,
          whitelabel: whitelabel || null,
        })
      } else {
        flagsmith.logout()
      }
    }
  }, [username, flagsmithWhitelabel, whitelabel, ready])

  const flagsmithOptions = useRef({
    cacheFlags: true,
    environmentID: Project.flagsmith,
    identity: username,
    onChange: () => {
      if (flagsmith.loadingState?.source !== 'NONE') {
        // Defer to avoid setState during render (React 18 strict mode)
        queueMicrotask(() => setReady(Date.now()))
      }
    },
    traits: data?.username ? { email: data.username } : undefined,
  })

  const companyId = subscriptionDetail?.company?.id

  useEffect(() => {
    if (!companyId) return
    if (flagsmith.getTrait('company_id') !== companyId) {
      flagsmith.setTrait('company_id', companyId)
    }
  }, [companyId])

  return (
    <FlagsmithProvider options={flagsmithOptions.current} flagsmith={flagsmith}>
      {!ready ? (
        <div className='text-center'>
          <Loader />
        </div>
      ) : (
        <>{children}</>
      )}
    </FlagsmithProvider>
  )
}

export default FeatureFlagProvider
